import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

import { curveValueAt, weeksFromDays } from "@/lib/curveLookup";
import type { StandardCurve } from "@/services/standardCurveService";

/**
 * A periodically-sampled flock measurement, plotted against its breed standard.
 *
 * Extracted from the Body Weight chart when egg weight and feed intake needed
 * the same treatment. Three hand-rolled copies of this is where the axis
 * convention drifts — and the convention is the whole point: the x-axis is the
 * days the flock was ACTUALLY measured, not the calendar. Body weight and egg
 * weight are weekly samples on nearly every farm, so a daily axis draws one
 * point per seven and six gaps.
 *
 * The flock's own line is never interpolated: every point on it is a real
 * reading. The standard is read at each of those same ages, so a point and its
 * benchmark are vertically aligned and the distance between them answers "are
 * we on curve?" — which is the only question this chart exists for.
 */
export interface SampleRow {
  date: string;
  ageDays: number;
  actual: number;
}

export default function SampledMeasureChart({
  title,
  rows,
  curve,
  yTitle,
  unit,
  decimals = 0,
  noCurveNote,
  emptyNote,
  truncatedNote,
  failed,
  height = 300,
}: {
  title: string;
  rows: SampleRow[];
  curve: StandardCurve | null;
  yTitle: string;
  unit: string;
  decimals?: number;
  /** Shown when there are readings but the breed has no published curve. */
  noCurveNote: string;
  /** Shown when nothing has been measured — say where the numbers come from. */
  emptyNote: string;
  truncatedNote?: string;
  /** A failed LOAD is not the same claim as "nobody measured". */
  failed?: boolean;
  height?: number;
}) {
  const points = curve?.points ?? EMPTY_POINTS;

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [2, 2], dashArray: [0, 5], curve: "straight" },
      markers: { size: [5, 0] },
      xaxis: {
        // Labelled by AGE. A standard is published against age, and "week 12"
        // is what an owner looks up in the breeder guide; a calendar date
        // makes them do the arithmetic.
        categories: rows.map((r) => `W${(r.ageDays / 7).toFixed(1)}`),
        title: { text: "Age (weeks)" },
        labels: { style: { fontSize: "11px" }, rotate: -45, rotateAlways: rows.length > 10 },
      },
      yaxis: {
        title: { text: yTitle },
        min: 0,
        labels: { formatter: (v: number) => v.toFixed(decimals) },
      },
      legend: { position: "top" },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => (v == null ? "—" : `${v.toFixed(decimals)} ${unit}`) },
      },
      colors: ["#23734b", "#8a9199"],
      dataLabels: { enabled: false },
    }),
    [rows, yTitle, unit, decimals],
  );

  const series = useMemo(() => {
    const actual = { name: "Measured", type: "line" as const, data: rows.map((r) => r.actual) };
    if (points.length === 0) return [actual];
    return [
      actual,
      {
        name: `Standard (${curve?.source})`,
        type: "line" as const,
        // curveValueAt returns null outside the curve's published range rather
        // than flat-lining at the nearest endpoint — a week-8 pullet held at
        // the week-18 value invents a target the flock is "failing".
        data: rows.map((r) => curveValueAt(points, weeksFromDays(r.ageDays))),
      },
    ];
  }, [rows, points, curve]);

  return (
    <div className="mb-6 card p-5 sm:p-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">{title}</h3>
        {rows.length > 0 && points.length === 0 && (
          <span className="text-xs text-warning-600 dark:text-warning-400">{noCurveNote}</span>
        )}
        {truncatedNote && (
          <span className="text-xs text-warning-600 dark:text-warning-400">{truncatedNote}</span>
        )}
      </div>
      {rows.length > 0 ? (
        <ReactApexChart options={options} series={series} type="line" height={height} />
      ) : failed ? (
        // NOT the empty state. "Nobody measured this" is a claim about the
        // farm, and printing it for a failed request tells an owner their
        // staff are skipping a job they may well be doing.
        <p className="py-10 text-center text-sm text-warning-600 dark:text-warning-400">
          Couldn't load this flock's measurements. Reload the page to try again —
          this doesn't mean none have been recorded.
        </p>
      ) : (
        <p className="py-10 text-center text-sm text-gray-400">{emptyNote}</p>
      )}
    </div>
  );
}

// Module-level so the `?? ` fallback is a stable reference and cannot retrigger
// the memos on every render.
const EMPTY_POINTS: StandardCurve["points"] = [];
